import React, {useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";

import {useUsers} from "@/context/UsersContext";
import {useAuth} from "@/context/AuthContext";

import EmployeesSkeleton from "./EmployeesSkeleton";
import Employee from "./Employee";

import {Card} from "@/components/ui/card";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";

export interface EmployeeI {
    uid: string;
    displayName: string | null;
    email: string | null;
    gender: string;
    birthDate: string;
    age: number | null;
    position: string;
    photoURL: string | null;
    dob: string;
    role: string;
    disabled: boolean;
}

const DEFAULT_PAGE_SIZE = 10;

export default function EmployeesPage() {
    const {users, loading, changeRole, deleteUser} = useUsers();
    const {role} = useAuth();

    const [searchParams, setSearchParams] = useSearchParams();

    const initialPage = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
    const initialPageSize = searchParams.get("pageSize")
        ? Number(searchParams.get("pageSize"))
        : DEFAULT_PAGE_SIZE;

    const [page, setPage] = useState<number>(Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1);
    const [pageSize, setPageSize] = useState<number>(
        Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : DEFAULT_PAGE_SIZE
    );

    // ВИДИМОСТЬ ДЛЯ ВСЕХ ЮЗЕРОВ:
    // убираем фильтр по superadmin вообще (пока так нужно)
    const visibleUsers = useMemo(() => users, [users]);

    useEffect(() => {
        setSearchParams({page: String(page), pageSize: String(pageSize)}, {replace: true});
    }, [page, pageSize, setSearchParams]);

    const totalPages = Math.max(1, Math.ceil(visibleUsers.length / pageSize));

    // если кто-то руками поставил page больше чем totalPages
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const displayedEmployees = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return visibleUsers.slice(startIndex, startIndex + pageSize);
    }, [page, pageSize, visibleUsers]);

    const pageNumbers = useMemo(() => {
        const nums: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) nums.push(i);
        return nums;
    }, [page, totalPages]);

    return (
        <div className="space-y-4 p-4 pb-24 sm:pb-4">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold tracking-tight truncate">Сотрудники</h2>
                    <p className="text-sm text-muted-foreground">
                        Роль: <span className="font-medium">{role || "unknown"}</span>
                    </p>
                </div>

                <Badge variant="secondary" className="shrink-0">
                    {loading ? "..." : visibleUsers.length}
                </Badge>
            </div>

            <Card className="p-3 sm:p-4">
                {loading ? (
                    <EmployeesSkeleton/>
                ) : (
                    <div className="space-y-3">
                        {displayedEmployees.map((employee) => (
                            <Employee
                                key={employee.uid}
                                employee={employee}
                                onDelete={deleteUser}
                                onRoleChange={changeRole}
                            />
                        ))}

                        {visibleUsers.length === 0 ? (
                            <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
                                Сотрудников пока нет
                            </div>
                        ) : null}

                        {/* Pagination (shadcn-friendly) */}
                        {visibleUsers.length > pageSize ? (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                                <div className="text-sm text-muted-foreground">
                                    Показано{" "}
                                    <span className="font-medium text-foreground">
                    {(page - 1) * pageSize + 1}
                  </span>
                                    {" "}–{" "}
                                    <span className="font-medium text-foreground">
                    {Math.min(page * pageSize, visibleUsers.length)}
                  </span>
                                    {" "}из{" "}
                                    <span className="font-medium text-foreground">
                    {visibleUsers.length}
                  </span>
                                </div>

                                <div className="flex items-center gap-2 flex-wrap">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                    >
                                        Назад
                                    </Button>

                                    {pageNumbers.map((n) => (
                                        <Button
                                            key={n}
                                            variant={n === page ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setPage(n)}
                                        >
                                            {n}
                                        </Button>
                                    ))}

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages}
                                    >
                                        Вперед
                                    </Button>

                                    <div className="flex items-center gap-2 ml-0 sm:ml-2">
                                        <span className="text-sm text-muted-foreground">На странице</span>
                                        <div className="flex gap-1">
                                            {[5, 10, 20, 50].map((s) => (
                                                <Button
                                                    key={s}
                                                    variant={s === pageSize ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => {
                                                        setPageSize(s);
                                                        setPage(1);
                                                    }}
                                                >
                                                    {s}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </Card>
        </div>
    );
}
